/* eslint-disable @typescript-eslint/return-await */
/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import axios from "axios";
import { AppThunk } from "@src/config/store";
import { ILogin, PortalAccount, UnauthenticatedActions } from "./slices";
import { formatRegionUrl } from "@src/utils/utils";
import { exposeCaptchaHeaders } from "@advapacs/common";

export interface PatientPortalCustomerResponse {
  accessLinkId?: string;
  region?: string;
  newAccessLinkId?: string;
  newRegion?: string;
  shouldRedirectToExam?: string;
}

export const refreshToken = (): Promise<void> => {
  try {
    return axios.post("/auth/refresh");
  } catch (error) {}
};

export const getUser = (): AppThunk => async (dispatch) => {
  try {
    dispatch(UnauthenticatedActions.startFetchSession());
    const userResponse = await axios.get(`/authenticatedPatient`);
    const response = userResponse?.data as PortalAccount;
    dispatch(UnauthenticatedActions.loginSuccess(response));
  } catch (err) {
    dispatch(UnauthenticatedActions.failFetchSession());
  }
};

export const logout = (): AppThunk => async (dispatch) => {
  try {
    await axios.post(`/authenticatedPatient/logout`);
    dispatch(UnauthenticatedActions.clearAuthentication());
  } catch (err) {
  } finally {
    dispatch(UnauthenticatedActions.clearAuthentication());
  }
};

export const submitLogin =
  (token?: string, props?: ILogin): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(UnauthenticatedActions.loginStart());
      await axios.post(`/auth/${token}/verify`, props);

      dispatch(getUser());
    } catch (err) {
      dispatch(
        UnauthenticatedActions.loginFail({
          err: err?.response?.data,
          pathname: "/",
        })
      );
    }
  };

export const getConfiguration =
  (token?: string, shouldDecode = true): AppThunk =>
  async (dispatch) => {
    try {
      let decoded = "";
      let tokenNoDash = "";
      if (shouldDecode) {
        decoded = atob(token);
        tokenNoDash = decoded.split(".")[1].replaceAll("-", "");
      } else {
        tokenNoDash = token.replaceAll("-", "");
      }
      const configurationResponse = await axios.get(
        `/auth/configuration/${tokenNoDash}`
      );
      dispatch(
        UnauthenticatedActions.getConfigurationSuccess({
          ...configurationResponse?.data,
          token: tokenNoDash,
        })
      );
    } catch (err) {
      dispatch(
        UnauthenticatedActions.getConfigurationFail(err?.response?.data)
      );
    }
  };

export const getCsrfToken = (): AppThunk => async (dispatch) => {
  try {
    dispatch(UnauthenticatedActions.getCsrfTokenStart());
    const crsfResponse = await axios.post(`/auth/csrf`);
    dispatch(UnauthenticatedActions.getCsrfTokenSuccess(crsfResponse?.data));
  } catch (err) {
    dispatch(UnauthenticatedActions.getCsrfTokenFail(err?.response?.data));
  } finally {
    dispatch(getUser());
  }
};

export const sendOTPToken =
  (
    token?: string,
    channel?: string,
    recaptchaToken?: string,
    turnstile?: boolean
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(UnauthenticatedActions.clearErrorMessage());
      dispatch(UnauthenticatedActions.getOTPTypeResponseStart());
      let rs;
      if (recaptchaToken) {
        rs = await axios.post(
          `/auth/${token}/send`,
          {
            channel,
          },
          {
            headers: exposeCaptchaHeaders(turnstile, recaptchaToken),
          }
        );
      } else {
        rs = await axios.post(`/auth/${token}/send`, {
          channel,
        });
      }

      dispatch(UnauthenticatedActions.getOTPTypeResponseSuccess(rs?.data));
    } catch (err) {
      if (
        // 1051 = RecaptchaRiskToken.
        // 1050 = RecaptchaInvalidToken.
        ["1051"].includes(err?.response.data.code)
      ) {
        dispatch(UnauthenticatedActions.clearSendOTPMethod());
        dispatch(UnauthenticatedActions.suspicousActivityStart());
        return;
      }

      dispatch(
        UnauthenticatedActions.getOTPTypeResponseFail(err?.response?.data)
      );
    }
  };

/**
 * Return customer info include region and customer id.
 * @param token => customerId, region
 * @returns
 */
// Example decoded/encoded token.
// https://www.w3docs.com/snippets/javascript/how-to-encode-and-decode-strings-with-base64-in-javascript.html#:~:text=In%20JavaScript%2C%20there%20are%20two%20functions%20for%20decoding,atob%20%28%29%2C%20which%20decodes%20a%20base64%20encoded%20string.
export const getCustomerIdFromToken = async (
  token: string
  //1. c2dwLjQ5NTQwODY2LTA5MmItNDYxMi1iNWVlLTAxNDJhZjM0MjM0ZQ==
  //2. c2dwLjc5MmFlNGFiLTQ5ODktNDBiMS05NmFkLWRiMDhiZGZlNTBlNg==
): Promise<PatientPortalCustomerResponse> => {
  const isLogged = localStorage.getItem("logged") === "true";
  // this one will store date when reload page for next time after logged.
  const dataFromStorage: PatientPortalCustomerResponse = {
    accessLinkId: localStorage.getItem("accessId"),
    region: localStorage.getItem(`${window.location.host}.region`),
  };

  try {
    // CASE 1: No token && Unauthenticated.
    if (!token && !isLogged) {
      return Promise.reject({ code: "1993" });
    }

    // CASE 2: No token && authenticated.
    if (
      !token &&
      isLogged &&
      dataFromStorage.accessLinkId &&
      dataFromStorage.region
    ) {
      return Promise.resolve(dataFromStorage);
    }

    // CASE 3: Has token && Unauthenticated.
    const decoded = atob(token);
    const [region, accessLinkId] = decoded?.split(".");

    if (!isLogged) {
      if (!region || !accessLinkId) return Promise.reject({ code: "1992" }); // Decoded error.

      /** Verify Region */
      const axiosInstance = axios.create();
      axiosInstance.defaults.baseURL = formatRegionUrl(region);
      await axiosInstance.post("/auth/csrf");
      /**
       * Remove remember me key when detect new token or value equal false.
       * It will show modal remember after logged.
       */
      localStorage.removeItem("newAccessId");
      return Promise.resolve({ accessLinkId, region });
    } else {
      // CASE 4: has new token and authenticated.
      // @ts-ignore
      return Promise.resolve({
        ...dataFromStorage,
        newAccessLinkId: "",
        region: region,
      });
    }
  } catch {
    return Promise.reject({ code: "1992" }); // Decoded error.
  }
};
